import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import {
  PortfolioWorkModal,
  PortfolioWorkModalContent,
  ProjectListingModalContent,
} from '../../components';
import { createSlug, isFreedomlyUrl } from '../../util/urlHelpers';
import { linkifyHtml } from '../../util/richText';
import { MdOutlineOpenInFull } from 'react-icons/md';
import LeavingSiteModal from '../ActivityFeed/LeavingSiteModal';

import css from './ApplicationPanel.css';

const SectionProposal = ({
  intl,
  listing,
  listingDeleted,
  bestFit,
  similarWork,
  questions,
  portfolioWorks = [],
  loadingPortfolioWorks = false,
  onManageDisableScrolling,
  skillOptions = [],
  toolOptions = [],
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);
  const [originRect, setOriginRect] = useState(null);
  const [listingModalOpen, setListingModalOpen] = useState(false);
  const [listingOriginRect, setListingOriginRect] = useState(null);

  // URL of an external link the user clicked; drives the "leaving Freedomly" modal.
  const [pendingUrl, setPendingUrl] = useState(null);

  // Proposal sections are rendered with dangerouslySetInnerHTML, so link clicks are
  // intercepted with a delegated handler. Freedomly links open directly; external
  // links show a warning modal first (same behavior as chat links in ActivityFeed).
  const handleProposalClick = e => {
    const anchor = e.target.closest('a');
    if (!anchor || !e.currentTarget.contains(anchor)) {
      return;
    }
    const href = anchor.getAttribute('href') || '';
    if (!/^https?:\/\//i.test(href) || isFreedomlyUrl(href)) {
      return;
    }
    e.preventDefault();
    setPendingUrl(href);
  };

  const subHeadingFit = intl.formatMessage({ id: 'ApplicationPanel.bestFit' });
  const subHeadingWorkDone = intl.formatMessage({ id: 'ApplicationPanel.workDoneBefore' });
  const subHeadingQuestions = intl.formatMessage({ id: 'ApplicationPanel.projectQuestions' });
  const portfolioWorksHeading = intl.formatMessage({ id: 'ApplicationPanel.portfolioWorks' });
  const openProjectListingTitle = intl.formatMessage({ id: 'ApplicationPanel.openProjectListing' });
  const listingTitle = listing.attributes.title;
  const slug = createSlug(listingTitle);
  const id = listing.id.uuid;

  const handleWorkClick = (work, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setOriginRect(rect);
    setSelectedWork(work);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSelectedWork(null);
      setOriginRect(null);
    }, 300);
  };

  const handleListingClick = event => {
    setListingOriginRect(event.currentTarget.getBoundingClientRect());
    setListingModalOpen(true);
  };

  const handleCloseListingModal = () => {
    setListingModalOpen(false);
    setTimeout(() => {
      setListingOriginRect(null);
    }, 300);
  };

  return (
    <div className={css.proposal}>
      <div className={css.headingContainer}>
        <div className={css.projectListingSpan}>
          <FormattedMessage id="ApplicationPanel.projectListingLink" />
        </div>
        {!listingDeleted ? (
          <button
            type="button"
            className={css.projectListingLink}
            onClick={handleListingClick}
            title={openProjectListingTitle}
          >
            <span>{listingTitle}</span>
            <MdOutlineOpenInFull className={css.projectListingLinkIcon} />
          </button>
        ) : (
          <FormattedMessage id="TransactionPanel.deletedListingTitle" />
        )}
      </div>
      <div className={css.proposalContainer} onClick={handleProposalClick}>
        <div className={css.sectionContainer}>
          <div className={css.sectionHeading}>{subHeadingFit}</div>
          <div dangerouslySetInnerHTML={{ __html: linkifyHtml(bestFit) }} />
        </div>
        <div className={css.sectionContainer}>
          <div className={css.sectionHeading}>{subHeadingWorkDone}</div>
          {loadingPortfolioWorks ? (
            <>
              <br></br>
              <div className={css.sectionHeading}>{portfolioWorksHeading}</div>
              <p>Loading portfolio works...</p>
            </>
          ) : (
            portfolioWorks &&
            portfolioWorks.length > 0 && (
              <>
                <div className={css.portfolioWorksGrid}>
                  {portfolioWorks.map(work => {
                    const images = work.images || [];
                    const firstImage = images.length > 0 ? images[0] : null;

                    return (
                      <div
                        key={work.id.uuid}
                        className={css.portfolioWorkCard}
                        onClick={e => handleWorkClick(work, e)}
                        style={{ cursor: 'pointer' }}
                      >
                        {firstImage && (
                          <div className={css.portfolioWorkCardImage}>
                            <img
                              src={
                                firstImage.attributes.variants['horizontal']?.url ||
                                firstImage.attributes.variants['horizontal-2x']?.url
                              }
                              alt={work.title}
                            />
                            <div className={css.portfolioWorkCardIcon}>
                              <MdOutlineOpenInFull />
                            </div>
                            <div className={css.portfolioWorkCardTitle}>{work.title}</div>
                          </div>
                        )}
                        {!firstImage && (
                          <div className={css.portfolioWorkCardNoImage}>
                            <div className={css.portfolioWorkCardTitle}>{work.title}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}
          <div dangerouslySetInnerHTML={{ __html: linkifyHtml(similarWork) }} />
        </div>
        <div className={css.sectionContainer}>
          <div className={css.sectionHeading}>{subHeadingQuestions}</div>
          <div dangerouslySetInnerHTML={{ __html: linkifyHtml(questions) }} />
        </div>
      </div>
      <PortfolioWorkModal
        id="portfolioWorkModal"
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onManageDisableScrolling={onManageDisableScrolling}
        originRect={originRect}
        privateWork={selectedWork?.attributes?.publicData?.privateWork}
        workId={selectedWork?.id?.uuid}
        usePortal
      >
        {selectedWork && (
          <PortfolioWorkModalContent
            work={selectedWork}
            skillOptions={skillOptions}
            toolOptions={toolOptions}
            intl={intl}
          />
        )}
      </PortfolioWorkModal>
      {!listingDeleted ? (
        <PortfolioWorkModal
          id="projectListingModal"
          isOpen={listingModalOpen}
          onClose={handleCloseListingModal}
          onManageDisableScrolling={onManageDisableScrolling}
          originRect={listingOriginRect}
          pageLinkName="ProjectListingPage"
          pageLinkParams={{ id, slug }}
          pageLinkMessage={openProjectListingTitle}
          usePortal
        >
          {listingModalOpen && <ProjectListingModalContent listing={listing} />}
        </PortfolioWorkModal>
      ) : null}
      <LeavingSiteModal
        isOpen={!!pendingUrl}
        url={pendingUrl}
        onClose={() => setPendingUrl(null)}
        onConfirm={() => {
          if (pendingUrl) {
            window.open(pendingUrl, '_blank', 'noopener,noreferrer');
          }
          setPendingUrl(null);
        }}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
};

export default injectIntl(SectionProposal);
